import { CheckCircle, Copy, Users } from 'lucide-react'
import { FaFacebook } from 'react-icons/fa'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { facebookAdsListAccounts } from 'sprites-types/workflow/tools/facebookAdsListAccounts'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'

function FacebookAdsListAccountsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(facebookAdsListAccounts, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const accounts = result.accounts

  if (accounts.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Users className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No Facebook ad accounts found</div>
        </div>
      </div>
    )
  }

  const contextActions: ContextAction[] = [
    {
      id: 'copy-all-ids',
      label: 'Copy All Account IDs',
      icon: <Copy className="h-4 w-4" />,
      onClick: () => navigator.clipboard.writeText(accounts.map((a) => a.id).join('\n')),
    },
    {
      id: 'copy-all-names',
      label: 'Copy All Account Names',
      icon: <Copy className="h-4 w-4" />,
      onClick: () => navigator.clipboard.writeText(accounts.map((a) => a.name).join('\n')),
    },
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-start gap-4 px-4 py-3">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-blue-100 p-2">
              <CheckCircle className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <FaFacebook className="h-4 w-4 text-[#0064e0]" />
              <div className="text-sm font-semibold text-gray-900">
                {accounts.length} Ad Account{accounts.length === 1 ? '' : 's'} Found
              </div>
            </div>

            <div className="space-y-2">
              {accounts.map((account) => (
                <div
                  key={account.id}
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2"
                >
                  <div className="text-sm font-medium text-gray-900">{account.name}</div>
                  <div className="text-xs text-gray-500">ID: {account.id}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function downloadFacebookAdsListAccounts(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function facebookAdsListAccountsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(facebookAdsListAccounts, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const accounts = result.accounts

  if (accounts.length === 0) {
    return `## Facebook Ad Accounts

No ad accounts found.

---
`
  }

  const accountsList = accounts
    .map((account) => `- **${account.name}** (ID: ${account.id})`)
    .join('\n')

  return `## Facebook Ad Accounts (${accounts.length})

${accountsList}

---
`
}

export const FacebookAdsListAccountsDisplay = createWorkflowToolComponent(
  FacebookAdsListAccountsDisplayComponent,
  downloadFacebookAdsListAccounts,
  facebookAdsListAccountsToMarkdown
)
